// @ts-nocheck
import Editor from './../../../../components/Editor/Editor';
import PropTypes from 'prop-types';
import {
  getLineItemAttachmentUrl,
  getBucketFileUrl
} from '../../../../utils/resourcePathHelper';
import { pathReference } from '../../../../constants/files';
import { getLineItemServiceType } from '../../../../constants/lineItemServiceTypes';
import { useState, useMemo } from 'react';
import { CaretDown, CaretRight, FileText, X } from '@phosphor-icons/react';
import { Modal, Box, IconButton } from '@mui/material';
import { useOnlineStatus } from '../../context/OnlineStatusContext/OnlineStatusContext';
import { formatTravelDate, addTravelDays } from '../../../../utils/travelDate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as P from '@phosphor-icons/react';
import './Information.css';

const getAttachmentsFromService = (service = {}) => {
  const attachments = (service?.lineitemattachment ?? [])
    .sort((a, b) => a.seq - b.seq)
    .map((att) => {
      if (att.resourcepath) {
        return {
          name: att.attachmentname || 'Attachment',
          url: getLineItemAttachmentUrl({ resourcePath: att.resourcepath })
        };
      }
      return null;
    })
    .filter(Boolean);

  return attachments;
};

const iconExists = (iconName) => {
  return typeof P[iconName] === 'object';
};

const Information = ({ tripData = {} }) => {
  const [filter, setFilter] = useState('All Types');
  const [expandedDays, setExpandedDays] = useState({});
  const [selectedContent, setSelectedContent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isOnline } = useOnlineStatus();
  const { enableRefactoredServices } = useFlags();

  const {
    tripcontent = [],
    startdate,
    tripday = [],
    tripdetailedday = [],
    tripid,
    tenant
  } = tripData;

  // Process trip days to extract attachments
  const processedDays = useMemo(() => {
    if (!startdate || !tripday || tripday?.length === 0) {
      return [];
    }

    return tripday
      ?.map((day, dayIndex) => {
        const dayNumber = dayIndex + 1;
        const dayDate = formatTravelDate(
          addTravelDays(startdate, dayNumber - 1),
          'MMM d, yyyy'
        );

        const dayAttachments = [];

        if (enableRefactoredServices) {
          // New path: tripday[].lineitem[].lineitemattachment[]
          const { lineitem = [] } = day;

          lineitem?.forEach((service) => {
            const attachments = getAttachmentsFromService(service);

            const serviceTypeInfo = getLineItemServiceType(
              service?.servicetypecode
            );

            const iconref = serviceTypeInfo?.iconref;
            const serviceName = serviceTypeInfo?.name || 'Unknown Service';

            attachments?.forEach((attachment) => {
              dayAttachments.push({
                type: service?.servicetypecode || 'document',
                title: attachment?.name ?? '',
                url: attachment?.url ?? '',
                serviceTitle: serviceName,
                serviceName: serviceName,
                iconref: iconref
              });
            });
          });
        } else {
          // Legacy path: tripdetailedday[].tripdetaileddayservice[].servicefields[]
          const detailedDay = tripdetailedday.find(
            (d) => d?.tripdayid === day?.tripdayid
          );
          const { tripdetaileddayservice = [] } = detailedDay || {};

          tripdetaileddayservice.forEach((service) => {
            const { servicetype = {}, servicefields = [] } = service;
            const serviceTypeFields = servicetype?.servicetypefield ?? [];

            const mappedServiceFields = servicefields?.map((field) => {
              const foundServiceTypeField = serviceTypeFields.find(
                (item) =>
                  item?.servicefield?.servicefieldid ===
                    field?.servicefieldid ||
                  item?.servicefieldid === field?.servicefieldid
              );
              const key =
                foundServiceTypeField?.key ??
                foundServiceTypeField?.servicefield?.key ??
                '';
              return { ...field, key };
            });

            const uploadAttachmentsField = mappedServiceFields?.find(
              (f) => f.key === 'UPLOADATTACHMENTS'
            );
            if (!uploadAttachmentsField) return;

            const files = uploadAttachmentsField?.files || [];
            const rawValues =
              uploadAttachmentsField?.options?.map((opt) => opt?.value) || [];
            const attachmentFileNames = Array.isArray(rawValues)
              ? rawValues
              : rawValues
                ? [rawValues]
                : [];

            const serviceName = servicetype?.name || 'Unknown Service';
            const iconref = servicetype?.iconref;

            attachmentFileNames.forEach((fileName, index) => {
              if (!fileName) return;
              const url = getBucketFileUrl({
                fileName,
                pathReference: pathReference.DETAILED_DAY_SERVICE,
                tripId: tripid,
                tenantId: tenant?.tenantid
              });
              const attachmentName = files?.[index]?.originalname || fileName;
              dayAttachments.push({
                type: servicetype?.code || 'document',
                title: attachmentName,
                url: url ?? '',
                serviceTitle: serviceName,
                serviceName: serviceName,
                iconref: iconref
              });
            });
          });
        }

        return {
          id: day.tripdayid || `day-${dayNumber}`,
          day: dayNumber,
          date: dayDate,
          attachments: dayAttachments
        };
      })
      .filter((day) => day.attachments.length > 0); // Only include days with attachments
  }, [
    tripday,
    tripdetailedday,
    startdate,
    enableRefactoredServices,
    tripid,
    tenant
  ]);

  // Get unique filter options from processed days
  const getFilterOptions = () => {
    const types = new Set();
    processedDays.forEach((day) => {
      day.attachments?.forEach((attachment) => {
        types.add(attachment?.serviceName || attachment?.serviceTitle);
      });
    });
    return ['All Types', ...Array.from(types)];
  };

  // Filter attachments by selected type
  const filterAttachments = (attachments) => {
    if (filter === 'All Types') return attachments;
    return (
      attachments?.filter(
        (attachment) =>
          attachment.serviceName === filter ||
          attachment.serviceTitle === filter
      ) || []
    );
  };

  // Toggle day expansion
  const toggleDay = (dayId) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayId]: !prev[dayId]
    }));
  };

  // Handle content card click
  const handleContentClick = (content) => {
    setSelectedContent(content);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContent(null);
  };

  // Handle attachment download
  const handleAttachmentDownload = (attachment) => {
    if (attachment.url) {
      window.open(attachment.url, '_blank');
    }
  };

  // Get icon for attachment type
  const getAttachmentIcon = (iconref) => {
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;

    if (ServiceTypeIcon) {
      return <ServiceTypeIcon size={20} weight='regular' />;
    } else {
      return <FileText size={20} weight='regular' />;
    }
  };

  // Check if we should show the attachments section
  const shouldShowAttachments = startdate && processedDays.length > 0;

  return (
    <div
      className={`pwa-information ${!isOnline ? 'pwa-information__offline-margin' : ''}`}
    >
      {/* Information Sections */}
      <div className='pwa-information__sections'>
        {tripcontent?.map((section) => {
          const hasDescription =
            section?.description && section.description.length > 0;

          return (
            <div
              key={section?.tripcontentid}
              className='pwa-information__info-card pwa-information__info-card--clickable'
              onClick={() => handleContentClick(section)}
            >
              {section?.tripcontentimage?.[0]?.imageurl && (
                <div className='pwa-information__info-image'>
                  <img
                    src={section?.tripcontentimage?.[0]?.imageurl}
                    alt={section?.title || 'Trip content image'}
                  />
                </div>
              )}

              <div className='pwa-information__info-content'>
                <h3 className='pwa-information__info-title'>
                  {section?.title || ''}
                </h3>

                {hasDescription && (
                  <div className='pwa-information__info-description'>
                    <div className='pwa-information__description-preview'>
                      <Editor
                        editable={false}
                        isPreviewing={true}
                        content={section.description}
                        id={`pwa-info-description-editor-${section?.tripcontentid}`}
                        setDescription={() => {}}
                        setMarkdown={() => {}}
                        setIsPreviewing={() => {}}
                        showActions={false}
                        placeholder={``}
                        generatePrompt={null}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Trip Attachments Section - Only show if startdate exists and there are attachments */}
      {shouldShowAttachments && (
        <div className='pwa-information__section'>
          <div className='pwa-information__header'>
            <div>
              <h2 className='pwa-information__title'>Trip Attachments</h2>
              <p className='pwa-information__subtitle'>
                Important documents and vouchers for your trip
              </p>
            </div>

            {/* Filter Dropdown */}
            <div className='pwa-information__filter'>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className='pwa-information__filter-select'
              >
                {getFilterOptions().map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <CaretDown className='pwa-information__filter-icon' size={16} />
            </div>
          </div>

          {/* Trip Days */}
          <div className='pwa-information__days'>
            {processedDays.map((day) => {
              const filteredAttachments = filterAttachments(day.attachments);

              const isExpanded = expandedDays[day.id];

              if (filteredAttachments?.length === 0) return null;

              return (
                <div key={day.id} className='pwa-information__day'>
                  <button
                    className='pwa-information__day-header'
                    onClick={() => toggleDay(day.id)}
                  >
                    <span className='pwa-information__day-title'>
                      Day {day.day} - {day.date}
                    </span>
                    {isExpanded ? (
                      <CaretDown size={20} />
                    ) : (
                      <CaretRight size={20} />
                    )}
                  </button>

                  {isExpanded && (
                    <div className='pwa-information__day-content'>
                      {filteredAttachments.map(
                        (attachment, attachmentIndex) => (
                          <div
                            key={`${day.id}-${attachmentIndex}`}
                            className='pwa-information__attachment'
                            onClick={() => handleAttachmentDownload(attachment)}
                          >
                            <div className='pwa-information__attachment-icon'>
                              {getAttachmentIcon(attachment.iconref)}
                            </div>
                            <span className='pwa-information__attachment-title'>
                              {attachment.title}
                            </span>
                            <FileText size={20} />
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* No attachments message */}
          {processedDays.length === 0 && (
            <div className='pwa-information__no-attachments'>
              <FileText size={48} weight='light' />
              <p>No attachments found for this trip.</p>
            </div>
          )}
        </div>
      )}

      {/* MUI Modal for Full Content */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby='content-modal-title'
        aria-describedby='content-modal-description'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box className='pwa-information__modal'>
          {/* Modal Header */}
          <div className='pwa-information__modal-header'>
            <h2
              className='pwa-information__modal-title'
              id='content-modal-title'
            >
              {selectedContent?.title || 'Local Information'}
            </h2>
            <IconButton
              onClick={handleCloseModal}
              className='pwa-information__modal-close'
              aria-label='Close modal'
            >
              <X size={24} />
            </IconButton>
          </div>

          {/* Modal Content */}
          <div className='pwa-information__modal-content'>
            {selectedContent?.tripcontentimage?.[0]?.imageurl && (
              <div className='pwa-information__modal-image'>
                <img
                  src={selectedContent.tripcontentimage[0].imageurl}
                  alt={selectedContent.title || 'Content image'}
                />
              </div>
            )}

            <div className='pwa-information__modal-description'>
              <Editor
                editable={false}
                isPreviewing={true}
                content={selectedContent?.description || ''}
                id={`pwa-modal-description-editor-${selectedContent?.tripcontentid}`}
                setDescription={() => {}}
                setMarkdown={() => {}}
                setIsPreviewing={() => {}}
                showActions={false}
                placeholder={``}
                generatePrompt={null}
              />
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

Information.propTypes = {
  tripData: PropTypes.object.isRequired,
  info: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      day: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          url: PropTypes.string
        })
      )
    })
  )
};

export default Information;
